import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Icon, Modal } from 'semantic-ui-react';
import { usePopup, closePopup } from '../../lib/popup';

import Filters from './Filters';
import Memberships from '../Memberships';
import BoardMembershipPermissionsSelectStep from '../BoardMembershipPermissionsSelectStep';
import DeleteStep from '../DeleteStep';
import { ProjectBackgroundTypes } from '../../constants/Enums';

import styles from './BoardActions.module.scss';
import globalStyles from '../../styles.module.scss';

const BoardActions = React.memo(
  ({
    memberships,
    labels,
    filterUsers,
    filterLabels,
    allUsers,
    canEdit,
    canEditMemberships,
    canManagePointSystem,
    boardId,
    isPointSystemEnabled,
    pointSystem,
    contentTypeTemplates,
    background,
    backgroundImageUrl,
    onMembershipCreate,
    onMembershipUpdate,
    onMembershipDelete,
    onUserToFilterAdd,
    onUserFromFilterRemove,
    onLabelToFilterAdd,
    onLabelFromFilterRemove,
    onLabelCreate,
    onLabelUpdate,
    onLabelMove,
    onLabelDelete,
    onPointSystemEnabledSet,
    onPointSystemModalOpen,
    onContentTypeTemplatesFetch,
    onContentTypeTemplateApply,
  }) => {
    const DeletePopup = usePopup(DeleteStep);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);

    const handlePointSystemToggle = useCallback(() => {
      closePopup();

      if (isPointSystemEnabled) {
        onPointSystemEnabledSet(false);
        return;
      }

      const needsTemplatePick = !pointSystem?.sourcePointSystemId;
      onPointSystemEnabledSet(true);

      if (needsTemplatePick) {
        onContentTypeTemplatesFetch();
        setShowTemplatePicker(true);
      }
    }, [
      isPointSystemEnabled,
      onPointSystemEnabledSet,
      pointSystem?.sourcePointSystemId,
      onContentTypeTemplatesFetch,
    ]);

    const handleTemplatePickerClose = useCallback(() => {
      setShowTemplatePicker(false);
    }, []);

    const handleCreateNewTemplate = useCallback(() => {
      setShowTemplatePicker(false);
    }, []);

    const handleSelectTemplate = useCallback(
      (templateId) => {
        if (!templateId) {
          return;
        }
        onContentTypeTemplateApply(templateId);
        setShowTemplatePicker(false);
      },
      [onContentTypeTemplateApply],
    );

    return (
      <div
        className={classNames(
          styles.wrapper,
          background &&
            background.type === ProjectBackgroundTypes.GRADIENT &&
            globalStyles[`background${upperFirst(camelCase(background.name))}`],
        )}
        style={{
          background:
            background &&
            background.type === ProjectBackgroundTypes.IMAGE &&
            backgroundImageUrl &&
            `url("${backgroundImageUrl}") center / cover fixed`,
        }}
      >
        <div className={styles.actions}>
          <div className={styles.action}>
            <Memberships
              items={memberships}
              allUsers={allUsers}
              permissionsSelectStep={BoardMembershipPermissionsSelectStep}
              canEdit={canEditMemberships}
              onCreate={onMembershipCreate}
              onUpdate={onMembershipUpdate}
              onDelete={onMembershipDelete}
            />
          </div>
          <div className={styles.action}>
            <Filters
              users={filterUsers}
              labels={filterLabels}
              allBoardMemberships={memberships}
              allLabels={labels}
              canEdit={canEdit}
              onUserAdd={onUserToFilterAdd}
              onUserRemove={onUserFromFilterRemove}
              onLabelAdd={onLabelToFilterAdd}
              onLabelRemove={onLabelFromFilterRemove}
              onLabelCreate={onLabelCreate}
              onLabelUpdate={onLabelUpdate}
              onLabelMove={onLabelMove}
              onLabelDelete={onLabelDelete}
            />
          </div>
          {canManagePointSystem && boardId && (
            <div className={styles.action}>
              <DeletePopup
                title="Подтверждение"
                content={
                  isPointSystemEnabled
                    ? 'Отключить балльную систему?'
                    : 'Включить балльную систему?'
                }
                buttonContent={isPointSystemEnabled ? 'Отключить' : 'Включить'}
                onConfirm={handlePointSystemToggle}
              >
                <button
                  type="button"
                  className={classNames(
                    styles.pointSystemButton,
                    isPointSystemEnabled
                      ? styles.pointSystemButtonRed
                      : styles.pointSystemButtonGreen,
                  )}
                >
                  {isPointSystemEnabled
                    ? 'Отключить балльную систему'
                    : 'Включить балльную систему'}
                </button>
              </DeletePopup>
              {isPointSystemEnabled && (
                <button
                  type="button"
                  className={classNames(styles.pointSystemButton, styles.pointSystemButtonOrange)}
                  onClick={onPointSystemModalOpen}
                >
                  Настройки балльной системы
                </button>
              )}
            </div>
          )}
        </div>

        <Modal
          open={showTemplatePicker}
          size="tiny"
          centered={false}
          closeIcon
          closeOnDimmerClick={false}
          onClose={handleTemplatePickerClose}
          className={styles.templatePickerModal}
        >
          <Modal.Header>Выберите шаблон балльной системы</Modal.Header>
          <Modal.Content>
            <div className={styles.templatePickerHint}>
              Для балльной системы этой доски можно выбрать готовый шаблон или создать набор
              позже в настройках шаблонов.
            </div>
            <div className={styles.templatePickerList}>
              {(contentTypeTemplates || []).filter((t) => t.isHidden !== true).length === 0 ? (
                <div className={styles.templatePickerEmpty}>Нет доступных шаблонов</div>
              ) : (
                (contentTypeTemplates || [])
                  .filter((template) => template.isHidden !== true)
                  .map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={styles.templatePickerItem}
                    onClick={() => handleSelectTemplate(template.id)}
                  >
                    <span className={styles.templatePickerItemName}>
                      {template.name}
                      {template.isDefault && (
                        <span className={styles.templatePickerBadge}>default</span>
                      )}
                    </span>
                    <Icon name="chevron right" className={styles.templatePickerItemIcon} />
                  </button>
                ))
              )}
            </div>
          </Modal.Content>
          <Modal.Actions className={styles.templatePickerActions}>
            <Button type="button" onClick={handleTemplatePickerClose}>
              Закрыть
            </Button>
            <Button type="button" primary onClick={handleCreateNewTemplate}>
              Создать новую
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  },
);

BoardActions.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  memberships: PropTypes.array.isRequired,
  labels: PropTypes.array.isRequired,
  filterUsers: PropTypes.array.isRequired,
  filterLabels: PropTypes.array.isRequired,
  allUsers: PropTypes.array.isRequired,
  contentTypeTemplates: PropTypes.array,
  pointSystem: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */
  canEdit: PropTypes.bool.isRequired,
  canEditMemberships: PropTypes.bool.isRequired,
  canManagePointSystem: PropTypes.bool,
  boardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPointSystemEnabled: PropTypes.bool,
  background: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  backgroundImageUrl: PropTypes.string,
  onMembershipCreate: PropTypes.func.isRequired,
  onMembershipUpdate: PropTypes.func.isRequired,
  onMembershipDelete: PropTypes.func.isRequired,
  onUserToFilterAdd: PropTypes.func.isRequired,
  onUserFromFilterRemove: PropTypes.func.isRequired,
  onLabelToFilterAdd: PropTypes.func.isRequired,
  onLabelFromFilterRemove: PropTypes.func.isRequired,
  onLabelCreate: PropTypes.func.isRequired,
  onLabelUpdate: PropTypes.func.isRequired,
  onLabelMove: PropTypes.func.isRequired,
  onLabelDelete: PropTypes.func.isRequired,
  onPointSystemEnabledSet: PropTypes.func,
  onPointSystemModalOpen: PropTypes.func,
  onContentTypeTemplatesFetch: PropTypes.func,
  onContentTypeTemplateApply: PropTypes.func,
};

BoardActions.defaultProps = {
  canManagePointSystem: false,
  boardId: null,
  isPointSystemEnabled: false,
  pointSystem: null,
  contentTypeTemplates: [],
  background: null,
  backgroundImageUrl: null,
  onPointSystemEnabledSet: () => {},
  onPointSystemModalOpen: () => {},
  onContentTypeTemplatesFetch: () => {},
  onContentTypeTemplateApply: () => {},
};

export default BoardActions;
